import React, { useEffect, useState } from 'react';
import { PageLayout } from '../components/PageLayout';
import { GenericTanStackTable } from '../components/Table/GenericTable';
import { ColumnDef } from '@tanstack/react-table';
import { StockData } from 'src/models/stock-types';
import { getStockData } from 'src/services/stock.service';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingDotText from 'src/components/Loading/LoadingDotsText';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from 'src/components/Loading/LoadingSpinner';
import GenericErrorMessage from 'src/components/Error/GenericErrorMessage';
import SelectFilterComponent from 'src/components/Filtering/SelectFilteringComponent';
import useWindowSize from 'src/helpers/UseWindowSize';

export const StockTopRankedPage: React.FC = () => {
	const [stockData, setStockData] = useState<StockData[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedFilter, setSelectedFilter] = useState<string>('null');

	const { width } = useWindowSize();

	const { getAccessTokenSilently } = useAuth0();
	const navigate = useNavigate();

	const handleFilterChange = (filter: string) => {
		setSelectedFilter(filter);
	};

	const handleRowClick = (ticker: string) => {
		navigate(`/stockDetails/${ticker}/financialData`);
	};

	useEffect(() => {
		const fetchStockData = async () => {
			const accessToken = await getAccessTokenSilently();
			setIsLoading(true);
			setError(null);

			const { data, error } = await getStockData(
				accessToken,
				100,
				selectedFilter
			);

			if (data) {
				const dataWithRanking = data?.map((item: StockData, index: number) => ({
					ranking: index + 1,
					...item,
				}));
				setStockData(dataWithRanking || []);
				setIsLoading(false);
			}
			if (error) {
				setError(error.message);
				setIsLoading(false);
			}
		};

		fetchStockData();
	}, [selectedFilter]);

	const calculateOverallScore = (row: StockData): number | null => {
		const scores: number[] = [];

		// Add percentage-based scores (already 0-100 scale)
		if (
			row.insiderFilingScore !== undefined &&
			row.insiderFilingScore !== null
		) {
			scores.push(row.insiderFilingScore);
		}
		if (
			row.trendAdjustedScore !== undefined &&
			row.trendAdjustedScore !== null
		) {
			scores.push(row.trendAdjustedScore);
		}
		if (
			row.financialStrengthPercentage !== undefined &&
			row.financialStrengthPercentage !== null
		) {
			scores.push(row.financialStrengthPercentage);
		}
		if (
			row.tradeRecencyPercentile !== undefined &&
			row.tradeRecencyPercentile !== null
		) {
			scores.push(row.tradeRecencyPercentile);
		}
		if (row.scoreScaled !== undefined && row.scoreScaled !== null) {
			scores.push(row.scoreScaled * 3); // Weight: 1.5x
		}
		// Add ratingScore (typically 1-5 scale, normalize to 0-100)
		if (row.ratingScore !== undefined && row.ratingScore !== null) {
			scores.push((row.ratingScore / 5) * 100);
		}

		// Calculate average if we have any scores
		if (scores.length === 0) return null;
		return scores.reduce((sum, score) => sum + score, 0) / scores.length;
	};

	const allColumns: ColumnDef<StockData, any>[] = [
		{
			accessorKey: 'ranking',
			header: 'Ranking',
		},
		{ accessorKey: 'ticker', header: 'Ticker' },
		{ accessorKey: 'companyName', header: 'Company Name' },
		{
			accessorKey: 'insiderFilingScore',
			header: 'Filing Score',
			cell: (info) => `${info.getValue()?.toFixed(0) ?? 'N/A'}%`,
		},
		{
			accessorKey: 'trendAdjustedScore',
			header: 'JDK Score',
			cell: (info) => `${info.getValue()?.toFixed(0) ?? 'N/A'}%`,
		},
		{
			accessorKey: 'financialStrengthPercentage',
			header: 'Financial Stability',
			cell: (info) => `${info.getValue()?.toFixed(0) ?? 'N/A'}%`,
		},
		{
			accessorKey: 'scoreScaled',
			header: 'Buy Timing',
			cell: (info) => `${info.getValue()?.toFixed(0) ?? 'N/A'}%`,
		},
		{
			accessorKey: 'ratingScore',
			header: 'FMP Score',
		},
		{
			id: 'overallScore',
			header: 'Overall',
			accessorFn: (row) => calculateOverallScore(row),
			cell: (info) => {
				const score = info.getValue() as number | null;
				return score !== null ? `${score.toFixed(0)}%` : 'N/A';
			},
		},
		{
			accessorKey: 'tradeDate',
			header: 'Trade Date',
			cell: (info) => new Date(info.getValue() as string).toLocaleDateString(),
		},
		{
			accessorKey: 'numberOfInsiderTraders',
			header: 'Insider Traders',
		},
		{
			accessorKey: 'purchaseTotal',
			header: 'Purchase Total',
			cell: (info) =>
				`$${(info.getValue() as number)?.toLocaleString() ?? 'N/A'}`,
		},
		// {
		//     accessorKey: 'purchasePrice',
		//     header: 'Purchase Price',
		//     cell: (info) => `$${(info.getValue() as number).toFixed(0)}`,
		// },
		// { accessorKey: 'quantity', header: 'Quantity' },
		// {
		//     accessorKey: 'previouslyOwned',
		//     header: 'Previously Owned',
		// },
		{
			accessorKey: 'percentageOwned',
			header: 'Percentage Owned',
			cell: (info) => `${info.getValue() ?? 'N/A'}%`,
		},
	];
	const mobileColumns = allColumns.slice(1, 9);
	const columns = width < 768 ? mobileColumns : allColumns;

	return (
		<PageLayout>
			{error && (
				<GenericErrorMessage message={error} onClose={() => setError(null)} />
			)}
			<div className="top_ranked_content-layout">
				{error && <p className="error-message">{error}</p>}
				{isLoading ? (
					<div>
						{/* <div>
							<h3 className="yearly-scores-title">
								<LoadingDotText text={`Calculating Top Filings`} />
							</h3>
						</div> */}
						<LoadingSpinner message={``} />
					</div>
				) : (
					<div>
						<h2 className="stock-filings__title">
							Top Insider Filings{' '}
							<p className="hero-banner__headline__3">
								Discover the best-performing stocks ranked by insider trading
								activity and algorithmic analysis of company finances
							</p>
						</h2>
						<SelectFilterComponent
							onFilterChange={handleFilterChange}
							currentFilter={selectedFilter}
						/>
						<GenericTanStackTable
							data={stockData}
							columns={columns}
							onRowClick={(row) => handleRowClick(row.ticker)}
						/>
					</div>
				)}
			</div>
		</PageLayout>
	);
};
